import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  getMe,
  getMyPlatformAccess,
  useGetMe,
  useGetMyPlatformAccess,
  useGetMyFeatures,
} from "@workspace/api-client-react";
import type { AuthedUser, FeatureKey } from "@workspace/api-client-react";
import { withRequestTimeout } from "./with-request-timeout";

const SALES_FINANCE_FIELDS = new Set([
  "totalCost",
  "grossProfit",
  "netProfit",
  "trueProfit",
  "trueMargin",
  "margin",
  "additionalCostsGbp",
  "additionalCostsReason",
  "shippingCost",
  "platformFees",
  "outputVat",
  "vatAdjustedProfit",
  "unitCost",
  "lineProfit",
  "effectiveCostSource",
]);

/**
 * Remove finance-only values from Sales data already held by the browser.
 * The API remains authoritative; this prevents an old successful response
 * from surviving a permission downgrade in the active session.
 */
export function redactSalesFinanceData<T>(value: T): T {
  if (Array.isArray(value)) {
    return value.map((item) => redactSalesFinanceData(item)) as T;
  }
  if (!value || typeof value !== "object") return value;
  const redacted: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value)) {
    redacted[key] = SALES_FINANCE_FIELDS.has(key)
      ? null
      : redactSalesFinanceData(item);
  }
  return redacted as T;
}

const financePermissionByClient = new WeakMap<
  ReturnType<typeof useQueryClient>,
  { userId: string; canViewFinance: boolean }
>();

export type Permission =
  | "public_site.view"
  | "public_site.design"
  | "public_site.publish"
  | "view_dashboard"
  | "view_inventory"
  | "view_purchase_orders"
  | "view_sales"
  | "view_sale_price"
  | "finance.business_pnl.view"
  | "sales.payment.create_unpaid"
  | "sales.payment.mark_paid"
  | "sales.payment.add_payment"
  | "sales.payment.edit_due_date"
  | "sales.payment.write_off"
  | "sales.payment.override_unpaid_fulfilment"
  | "sales.payment.view_unpaid"
  | "sales.payment.view_history"
  | "view_packing"
  | "view_reports"
  | "view_marketing_reports"
  | "view_integrations"
  | "whatsapp_inbox.view"
  | "whatsapp_inbox.send"
  | "whatsapp_inbox.manage_settings"
  | "whatsapp_inbox.view_analytics"
  | "view_admin"
  | "edit_inventory"
  | "edit_purchase_orders"
  | "edit_sales"
  | "send_purchase_orders"
  | "manage_integrations"
  | "manage_users"
  | "view_activity_log"
  | "view_audit_log"
  | "edit_settings"
  | "manage_ebay_feedback"
  | "inventory.sales_reallocate"
  | "inventory.sales_reallocate_reverse"
  | "packing.manage"
  | "packing.pack"
  | "packing.confirm"
  | "packing.cancel"
  | "packing.override_complete"
  | "losses.manage"
  | "manage_vat_backfill"
  | "view_tracking_events"
  | "view_scrap"
  | "manage_scrap"
  | "ai.assistant.use"
  | "ai.history.view"
  | "catalogue_intelligence.view"
  | "catalogue_intelligence.check"
  | "catalogue_intelligence.review"
  | "catalogue_intelligence.apply"
  | "catalogue_intelligence.lock"
  | "catalogue_intelligence.bulk"
  | "catalogue_intelligence.configure"
  | "ai.settings.manage"
  | "ai.reports.generate"
  | "ai.reports.export"
  | "ai.questions.profit"
  | "ai.questions.hr"
  | "ai.actions.draft_po"
  | "ai.actions.create_task"
  | "ai.audit.view"
  | "tasks.view"
  | "tasks.view_team"
  | "tasks.create"
  | "tasks.assign_others"
  | "tasks.edit_any"
  | "tasks.delete"
  | "tasks.complete"
  | "tasks.comment"
  | "tasks.projects.view"
  | "tasks.projects.manage"
  | "operations.next_action.view"
  | "operations.next_action.manage"
  | "operations.search.use"
  | "operations.checklists.view"
  | "operations.checklists.complete"
  | "operations.checklists.manage"
  | "operations.feed.view"
  | "operations.status_board.view"
  | "operations.status_board.manage"
  | "operations.friction_analytics.view"
  | "operations.fulfilment_reports.view"
  | "notifications.view_own"
  | "notifications.manage_own_prefs"
  | "notifications.manage_tenant_defaults"
  | "notifications.manage_thresholds"
  | "notifications.send_manual"
  | "notifications.view_delivery_logs"
  | "notifications.manage_mandatory"
  | "hr.view"
  | "hr.staff.view"
  | "hr.staff.create"
  | "hr.staff.edit"
  | "hr.staff.archive"
  | "hr.pay.view"
  | "hr.pay.edit"
  | "hr.bank.view"
  | "hr.bank.edit"
  | "hr.documents.upload"
  | "hr.documents.view"
  | "hr.documents.download"
  | "hr.documents.delete"
  | "hr.onboarding.manage"
  | "hr.training.manage"
  | "hr.incidents.view"
  | "hr.incidents.create"
  | "hr.incidents.edit"
  | "hr.hours.approve"
  | "hr.timeoff.approve"
  | "hr.changes.approve"
  | "hr.settings.manage"
  | "hr.audit.view"
  | "hr.performance.view"
  | "hr.payments.manage"
  | "hr.self.view_record"
  | "hr.self.request_changes"
  | "hr.self.request_time_off"
  | "hr.self.view_documents"
  | "hr.self.view_onboarding"
  | "hr.self.view_training"
  | "hr.self.view_absences"
  | "hr.self.view_communications"
  | "quickbooks.view"
  | "quickbooks.connect"
  | "quickbooks.disconnect"
  | "quickbooks.mappings.manage"
  | "quickbooks.sync.run"
  | "quickbooks.sync_logs.view"
  | "quickbooks.sync.retry"
  | "quickbooks.vat_reports.view"
  | "xero.view"
  | "xero.connect"
  | "xero.disconnect"
  | "xero.sync.run"
  | "xero.sync_logs.view"
  | "xero.sync.retry"
  | "xero.settings.manage"
  | "returns.view"
  | "returns.create_manual"
  | "returns.ebay.manage"
  | "returns.tiktok.manage"
  | "returns.evidence.record"
  | "returns.evidence.view"
  | "returns.evidence.delete"
  | "returns.evidence.share"
  | "returns.inspect"
  | "returns.approve_outcome"
  | "returns.reject"
  | "returns.refund.approve"
  | "returns.inventory.action"
  | "returns.settings.manage"
  | "returns.audit.view"
  | "returns.reports.view"
  // Pre-Orders module (Task #2058) — mirrors lib/db users.ts.
  | "pre_orders.view"
  | "pre_orders.manage"
  | "pre_orders.settings.manage"
  | "exceptions.view"
  | "exceptions.manage"
  | "exceptions.manage_rules"
  // Customer Commerce Portal (Task #2242) — mirrors lib/db users.ts.
  // FE mirror is affordance-hiding only; every commerce route re-checks
  // the feature flag AND the permission server-side.
  | "commerce.view"
  | "markets.view"
  | "commerce.manage"
  | "commerce.product.publish"
  | "commerce.product.merchandise"
  | "commerce.categories.manage"
  | "commerce.collections.manage"
  | "commerce.pricing.view"
  | "commerce.pricing.edit"
  | "commerce.pricing.override"
  | "commerce.box.view"
  | "commerce.box.edit"
  | "commerce.box.reserve"
  | "commerce.box.price_override"
  | "commerce.pre_order.view"
  | "commerce.pre_order.manage"
  | "commerce.payment.view"
  | "commerce.payment.mark_received"
  | "commerce.portal.design"
  | "commerce.portal.settings"
  | "commerce.customer.impersonate"
  | "commerce.analytics.view"
  | "customers.behaviour.view"
  | "customers.behaviour.settings.manage"
  | "customers.behaviour.segments.manage"
  | "customers.behaviour.export"
  | "customers.reorder.view"
  | "customer_portfolios.view"
  | "customer_portfolios.manage"
  | "customer_portfolios.transactions.manage"
  | "customer_portfolios.valuations.view"
  | "customer_portfolios.settings.manage"
  | "customer_portfolios.audit.view"
  | "customer_portfolios.products.manage"
  | "customer_portfolios.accounts.manage"
  | "customer_portfolios.accounts.support"
  | "markets.view"
  | "markets.calculate"
  | "markets.history.view"
  | "markets.offers.manage"
  | "markets.settings.manage"
  | "markets.alerts.manage"
  | "markets.content.export"
  | "streaming.overlays.manage"
  // Affiliate Payout Management (Task #1443) — mirrors lib/db users.ts.
  | "affiliates.view"
  | "affiliates.create"
  | "affiliates.edit"
  | "affiliates.rules.manage"
  | "affiliates.rules.approve"
  | "affiliates.calculations.view"
  | "affiliates.payouts.approve"
  | "affiliates.batches.create"
  | "affiliates.batches.mark_paid"
  | "affiliates.batches.reopen"
  | "affiliates.adjustments.create"
  | "affiliates.statements.manage"
  | "affiliates.reports.view"
  | "affiliates.settings.manage"
  | "affiliates.audit.view"
  // Consignment Profit Split Management (Task #1444) — mirrors lib/db users.ts.
  | "consignment_splits.view"
  | "consignment_splits.rules.manage"
  | "consignment_splits.rules.approve"
  | "consignment_splits.calculations.view"
  | "consignment_splits.adjustments.create"
  | "consignment_splits.settlements.manage"
  | "consignment_splits.settlements.approve"
  | "consignment_splits.settlements.mark_paid"
  | "consignment_splits.settlements.reopen"
  | "consignment_splits.reports.view"
  | "consignment_splits.settings.manage"
  | "consignment_splits.audit.view"
  // AI Product Copy Generator (Task #1546) — mirrors lib/db users.ts.
  | "ai.product_copy.view"
  | "ai.product_copy.generate"
  | "ai.product_copy.edit_factsheet"
  | "ai.product_copy.fetch_sources"
  | "ai.product_copy.history"
  | "ai.product_copy.approve"
  | "ai.product_copy.apply_title"
  | "ai.product_copy.apply_description"
  | "ai.product_copy.manage_profiles"
  | "tiktok_shop.view"
  | "tiktok_shop.connect"
  | "tiktok_shop.developer.manage"
  | "tiktok_shop.disconnect"
  | "tiktok_shop.settings.manage"
  | "tiktok_shop.sync_logs.view"
  | "tiktok_shop.sync.retry"
  | "tiktok_shop.orders.sync"
  | "tiktok_shop.stock.sync"
  | "tiktok_shop.tracking.push"
  | "tiktok_shop.products.push"
  | "tiktok_shop.mappings.manage"
  | "tiktok_shop.messages.view"
  | "tiktok_shop.messages.reply"
  | "tiktok_shop.fulfilment.push"
  | "advanced_analytics.view"
  | "advanced_analytics.export"
  | "advanced_analytics.saved_views.manage"
  | "customers.clv.view"
  | "customers.merge"
  | "customers.identity.unlink"
  | "customers.merge_logs.view"
  | "customers.analytics.export"
  | "customers.profile_360.view"
  | "forecasting.view"
  | "forecasting.settings.manage"
  | "forecasting.run"
  | "forecasting.recommendations.manage"
  | "forecasting.export"
  | "intelligence.view"
  | "intelligence.settings.manage"
  | "intelligence.run"
  | "intelligence.manage"
  | "purchasing.view"
  | "purchasing.approve"
  | "automations.view"
  | "automations.create"
  | "automations.edit"
  | "automations.delete"
  | "pricing.view"
  | "pricing.rules.manage"
  | "pricing.settings.manage"
  | "pricing.run"
  | "pricing.approve"
  | "pricing.export"
  | "pricing.publish"
  | "pricing.override_margin"
  | "pricing.costs.view"
  | "pricing.reference.view"
  | "pricing.bulk"
  | "ai_agents.view"
  | "ai_agents.run"
  | "ai_agents.approve"
  | "ai_agents.schedules.manage"
  | "ai_agents.settings.manage"
  | "documents.templates.view"
  | "documents.templates.create"
  | "documents.templates.edit"
  | "documents.generate"
  | "documents.view"
  | "documents.email"
  | "documents.delete"
  | "asset.view"
  | "asset.create"
  | "asset.edit"
  | "asset.archive"
  | "asset.assign"
  | "asset.documents.upload"
  | "asset.documents.view"
  | "asset.documents.delete"
  | "asset.settings.manage"
  | "asset.audit.view"
  | "asset.reports.view"
  | "warehouse.view"
  | "warehouse.locations.manage"
  | "warehouse.scan"
  | "warehouse.cycle_count"
  | "compliance.view"
  | "compliance.manage"
  | "compliance.documents.view"
  | "compliance.documents.manage"
  | "compliance.audit.view"
  | "postage.view"
  | "postage.services.manage"
  | "postage.shipment.create"
  | "postage.labels.generate"
  | "postage.labels.batch"
  | "postage.combine.manage"
  | "postage.pick_lists.generate"
  | "postage.tracking.push"
  | "postage.couriers.manage"
  | "postage.settings.manage"
  | "postage.reports.view"
  | "postage.audit.view"
  | "social.view"
  | "social.accounts.view"
  | "social.accounts.connect"
  | "social.accounts.disconnect"
  | "social.posts.compose"
  | "social.posts.schedule"
  | "social.posts.publish"
  | "social.posts.approve"
  | "social.posts.reject"
  | "social.posts.delete"
  | "social.media.view"
  | "social.media.upload"
  | "social.media.delete"
  | "social.assets.approve"
  | "social.removebg.use"
  | "social.designs.view"
  | "social.designs.create"
  | "social.designs.edit"
  | "social.designs.delete"
  | "social.studio.view"
  | "social.templates.manage"
  | "social.brandkit.manage"
  | "social.campaigns.manage"
  | "social.analytics.view"
  | "social.analytics.export"
  | "social.inbox.view"
  | "social.inbox.reply"
  | "social.audit.view"
  | "social.settings.manage"
  | "shopify.field_mappings.view"
  | "shopify.field_mappings.manage"
  | "shopify.field_mappings.manage_profiles"
  | "shopify.field_mappings.test"
  | "shopify.field_mappings.push"
  | "tenant_account.view"
  | "tenant_account.manage"
  | "tenant_account.request_feature"
  | "tenant_account.requests.view"
  | "tenant_account.request_plan_change"
  | "tenant_account.support.view"
  | "tenant_account.support.create"
  | "tenant_account.support.reply"
  | "tenant_account.support.manage"
  | "tenant_account.billing.view"
  | "tenant_account.billing.pay_invoices"
  | "tenant_account.billing.manage_payment_methods"
  | "tenant_account.users.view"
  | "tenant_account.users.manage_users"
  | "tenant_account.users.invite_users"
  | "tenant_account.business_details.manage"
  | "evidence.view"
  | "evidence.share"
  | "evidence.manage"
  | "view_inventory_valuation"
  | "set_valuation_method"
  | "mark_product_premium"
  | "set_hybrid_components"
  | "enter_premium_valuation"
  | "approve_premium_valuation"
  | "use_custom_valuation"
  | "bulk_update_valuation_methods"
  | "view_premium_inventory_value"
  | "view_commodity_inventory_value"
  | "view_portfolio_split"
  | "run_valuation_recalculation"
  | "view_valuation_audit_log"
  | "manage_valuation_settings"
  | "approve_valuation_changes"
  | "view_portfolio_snapshot"
  | "manage_portfolio_snapshots"
  | "recalculate_portfolio_valuation"
  | "export_valuation_report"
  | "export_valuation_reports"
  | "view_valuation_dashboard_widgets"
  | "configure_spot_price_feeds"
  | "override_valuation_method"
  | "view_valuation_migration_status"
  | "manage_valuation_rules"
  | "enter_premium_valuation"
  | "approve_premium_valuation"
  // AI Product Copy — Phase 5 additions
  | "ai.product_copy.bulk_generate"
  | "ai.product_copy.push_to_marketplaces"
  // Image Studio (Task #1869) — mirrors lib/db users.ts.
  | "image_studio.view"
  | "image_studio.edit"
  | "image_studio.export"
  | "image_studio.manage_settings"
  | "image_studio.use_ai"
  // Scene Builder (Task #1871 Image Studio Phase 3) — mirrors lib/db users.ts.
  | "scene_builder.view"
  | "scene_builder.edit"
  | "scene_builder.export"
  | "scene_builder.manage_templates"
  // Inventory Lots & Cost Layers (Task #2172) — mirrors lib/db users.ts.
  | "lots.view"
  | "lots.create"
  | "lots.adjust"
  | "lots.view_cost"
  | "lots.edit_cost"
  | "lots.correct_cost"
  | "lots.manual_allocate"
  | "lots.manage_costing_settings";

/**
 * The 5 finer-grained packing permissions (Task #22), in display order, with
 * the short helper label admins see when reading or granting them. Used by:
 *   - `pages/admin.tsx` Roles & permissions cards (grouped subsection)
 *   - `pages/admin/user-permissions.tsx` Packing permissions cheat sheet
 * Kept in one place so the labels stay in sync across both surfaces.
 */
/**
 * Runtime mirror of the `Permission` union — used by the per-tenant override
 * editor to render a checklist of every grantable permission. Keep in sync
 * with the union above AND with the backend `PERMISSIONS` enum in
 * `lib/db/src/schema/users.ts`. The server validates against its own enum,
 * so an extra/missing entry here is a UX bug, never a security bug.
 */
export const ALL_PERMISSIONS = [
  "view_dashboard",
  "view_inventory",
  "view_purchase_orders",
  "view_sales",
  "view_sale_price",
  "sales.payment.create_unpaid",
  "sales.payment.mark_paid",
  "sales.payment.add_payment",
  "sales.payment.edit_due_date",
  "sales.payment.write_off",
  "sales.payment.override_unpaid_fulfilment",
  "sales.payment.view_unpaid",
  "sales.payment.view_history",
  "view_packing",
  "view_reports",
  "view_marketing_reports",
  "view_integrations",
  "whatsapp_inbox.view",
  "whatsapp_inbox.send",
  "whatsapp_inbox.manage_settings",
  "whatsapp_inbox.view_analytics",
  "view_admin",
  "edit_inventory",
  "edit_purchase_orders",
  "edit_sales",
  "send_purchase_orders",
  "manage_integrations",
  "manage_users",
  "view_activity_log",
  "view_audit_log",
  "edit_settings",
  "manage_ebay_feedback",
  "inventory.sales_reallocate",
  "inventory.sales_reallocate_reverse",
  "packing.manage",
  "packing.pack",
  "packing.confirm",
  "packing.cancel",
  "packing.override_complete",
  "losses.manage",
  "manage_vat_backfill",
  "view_tracking_events",
  "view_scrap",
  "manage_scrap",
  "ai.assistant.use",
  "ai.history.view",
  "ai.settings.manage",
  "ai.reports.generate",
  "ai.reports.export",
  "ai.questions.profit",
  "ai.questions.hr",
  "ai.actions.draft_po",
  "ai.actions.create_task",
  "ai.audit.view",
  "tasks.view",
  "tasks.view_team",
  "tasks.create",
  "tasks.assign_others",
  "tasks.edit_any",
  "tasks.delete",
  "tasks.complete",
  "tasks.comment",
  "tasks.projects.view",
  "tasks.projects.manage",
  "notifications.view_own",
  "notifications.manage_own_prefs",
  "notifications.manage_tenant_defaults",
  "notifications.manage_thresholds",
  "notifications.send_manual",
  "notifications.view_delivery_logs",
  "notifications.manage_mandatory",
  "hr.view",
  "hr.staff.view",
  "hr.staff.create",
  "hr.staff.edit",
  "hr.staff.archive",
  "hr.pay.view",
  "hr.pay.edit",
  "hr.bank.view",
  "hr.bank.edit",
  "hr.documents.upload",
  "hr.documents.view",
  "hr.documents.download",
  "hr.documents.delete",
  "hr.onboarding.manage",
  "hr.training.manage",
  "hr.incidents.view",
  "hr.incidents.create",
  "hr.incidents.edit",
  "hr.hours.approve",
  "hr.timeoff.approve",
  "hr.changes.approve",
  "hr.settings.manage",
  "hr.audit.view",
  "hr.performance.view",
  "hr.payments.manage",
  "hr.self.view_record",
  "hr.self.request_changes",
  "hr.self.request_time_off",
  "hr.self.view_documents",
  "hr.self.view_onboarding",
  "hr.self.view_training",
  "hr.self.view_absences",
  "hr.self.view_communications",
  "quickbooks.view",
  "quickbooks.connect",
  "quickbooks.disconnect",
  "quickbooks.mappings.manage",
  "quickbooks.sync.run",
  "quickbooks.sync_logs.view",
  "quickbooks.sync.retry",
  "quickbooks.vat_reports.view",
  "xero.view",
  "xero.connect",
  "xero.disconnect",
  "xero.sync.run",
  "xero.sync_logs.view",
  "xero.sync.retry",
  "xero.settings.manage",
  "returns.view",
  "returns.create_manual",
  "returns.ebay.manage",
  "returns.tiktok.manage",
  "returns.evidence.record",
  "returns.evidence.view",
  "returns.evidence.delete",
  "returns.evidence.share",
  "returns.inspect",
  "returns.approve_outcome",
  "returns.reject",
  "returns.refund.approve",
  "returns.inventory.action",
  "returns.settings.manage",
  "returns.audit.view",
  "returns.reports.view",
  "pre_orders.view",
  "pre_orders.manage",
  "pre_orders.settings.manage",
  "exceptions.view",
  "exceptions.manage",
  "exceptions.manage_rules",
  // Customer Commerce Portal (Task #2242) — mirrors lib/db users.ts.
  "commerce.view",
  "markets.view",
  "markets.alerts.manage",
  "markets.content.export",
  "commerce.manage",
  "commerce.product.publish",
  "commerce.product.merchandise",
  "commerce.categories.manage",
  "commerce.collections.manage",
  "commerce.pricing.view",
  "commerce.pricing.edit",
  "commerce.pricing.override",
  "commerce.box.view",
  "commerce.box.edit",
  "commerce.box.reserve",
  "commerce.box.price_override",
  "commerce.pre_order.view",
  "commerce.pre_order.manage",
  "commerce.payment.view",
  "commerce.payment.mark_received",
  "commerce.portal.design",
  "commerce.portal.settings",
  "commerce.customer.impersonate",
  "commerce.analytics.view",
  "customers.behaviour.view",
  "customers.behaviour.settings.manage",
  "customers.behaviour.segments.manage",
  "customers.behaviour.export",
  "customers.reorder.view",
  "customer_portfolios.view",
  "customer_portfolios.manage",
  "customer_portfolios.transactions.manage",
  "customer_portfolios.valuations.view",
  "customer_portfolios.settings.manage",
  "customer_portfolios.audit.view",
  "customer_portfolios.products.manage",
  "customer_portfolios.accounts.manage",
  "customer_portfolios.accounts.support",
  "streaming.overlays.manage",
  "tiktok_shop.view",
  "tiktok_shop.connect",
  "tiktok_shop.developer.manage",
  "tiktok_shop.disconnect",
  "tiktok_shop.settings.manage",
  "tiktok_shop.sync_logs.view",
  "tiktok_shop.sync.retry",
  "tiktok_shop.orders.sync",
  "tiktok_shop.stock.sync",
  "tiktok_shop.tracking.push",
  "tiktok_shop.products.push",
  "tiktok_shop.mappings.manage",
  "tiktok_shop.messages.view",
  "tiktok_shop.messages.reply",
  "tiktok_shop.fulfilment.push",
  "advanced_analytics.view",
  "advanced_analytics.export",
  "advanced_analytics.saved_views.manage",
  "customers.clv.view",
  "customers.merge",
  "customers.identity.unlink",
  "customers.merge_logs.view",
  "customers.analytics.export",
  "customers.profile_360.view",
  "forecasting.view",
  "forecasting.settings.manage",
  "forecasting.run",
  "forecasting.recommendations.manage",
  "forecasting.export",
  "intelligence.view",
  "intelligence.settings.manage",
  "intelligence.run",
  "intelligence.manage",
  "purchasing.view",
  "purchasing.approve",
  "automations.view",
  "automations.create",
  "automations.edit",
  "automations.delete",
  "pricing.view",
  "pricing.rules.manage",
  "pricing.settings.manage",
  "pricing.run",
  "pricing.approve",
  "pricing.export",
  "pricing.publish",
  "pricing.override_margin",
  "pricing.costs.view",
  "pricing.reference.view",
  "pricing.bulk",
  "ai_agents.view",
  "ai_agents.run",
  "ai_agents.approve",
  "ai_agents.schedules.manage",
  "ai_agents.settings.manage",
  "documents.templates.view",
  "documents.templates.create",
  "documents.templates.edit",
  "documents.generate",
  "documents.view",
  "documents.email",
  "documents.delete",
  "asset.view",
  "asset.create",
  "asset.edit",
  "asset.archive",
  "asset.assign",
  "asset.documents.upload",
  "asset.documents.view",
  "asset.documents.delete",
  "asset.settings.manage",
  "asset.audit.view",
  "asset.reports.view",
  "warehouse.view",
  "warehouse.locations.manage",
  "warehouse.scan",
  "warehouse.cycle_count",
  "compliance.view",
  "compliance.manage",
  "compliance.documents.view",
  "compliance.documents.manage",
  "compliance.audit.view",
  "postage.view",
  "postage.services.manage",
  "postage.shipment.create",
  "postage.labels.generate",
  "postage.labels.batch",
  "postage.combine.manage",
  "postage.pick_lists.generate",
  "postage.tracking.push",
  "postage.couriers.manage",
  "postage.settings.manage",
  "postage.reports.view",
  "postage.audit.view",
  "shopify.field_mappings.view",
  "shopify.field_mappings.manage",
  "shopify.field_mappings.manage_profiles",
  "shopify.field_mappings.test",
  "shopify.field_mappings.push",
  "tenant_account.view",
  "tenant_account.manage",
  "tenant_account.request_feature",
  "tenant_account.requests.view",
  "tenant_account.request_plan_change",
  "tenant_account.support.view",
  "tenant_account.support.create",
  "tenant_account.support.reply",
  "tenant_account.support.manage",
  "tenant_account.billing.view",
  "tenant_account.billing.pay_invoices",
  "tenant_account.billing.manage_payment_methods",
  "tenant_account.users.view",
  "tenant_account.users.manage_users",
  "tenant_account.users.invite_users",
  "tenant_account.business_details.manage",
  "evidence.view",
  "evidence.share",
  "evidence.manage",
  // AI Product Copy Generator (Task #1546) — mirrors lib/db users.ts.
  "ai.product_copy.view",
  "ai.product_copy.generate",
  "ai.product_copy.edit_factsheet",
  "ai.product_copy.fetch_sources",
  "ai.product_copy.history",
  "ai.product_copy.approve",
  "ai.product_copy.apply_title",
  "ai.product_copy.apply_description",
  "ai.product_copy.manage_profiles",
  "image_studio.view",
  "image_studio.edit",
  "image_studio.export",
  "image_studio.manage_settings",
  "image_studio.use_ai",
  "scene_builder.view",
  "scene_builder.edit",
  "scene_builder.export",
  "scene_builder.manage_templates",
  // Inventory Valuation Engine (Phase 1 + Phase 2)
  "view_inventory_valuation",
  "set_valuation_method",
  "mark_product_premium",
  "set_hybrid_components",
  "enter_premium_valuation",
  "approve_premium_valuation",
  "use_custom_valuation",
  "bulk_update_valuation_methods",
  "view_premium_inventory_value",
  "view_commodity_inventory_value",
  "view_portfolio_split",
  "run_valuation_recalculation",
  "view_valuation_audit_log",
  "manage_valuation_settings",
  "approve_valuation_changes",
  "view_portfolio_snapshot",
  "manage_portfolio_snapshots",
  "recalculate_portfolio_valuation",
  "export_valuation_report",
  "export_valuation_reports",
  "view_valuation_dashboard_widgets",
  "configure_spot_price_feeds",
  "override_valuation_method",
  "view_valuation_migration_status",
  "manage_valuation_rules",
  // Inventory Lots & Cost Layers (Task #2172) — mirrors lib/db users.ts.
  "lots.view",
  "lots.create",
  "lots.adjust",
  "lots.view_cost",
  "lots.edit_cost",
  "lots.correct_cost",
  "lots.manual_allocate",
  "lots.manage_costing_settings",
] as const satisfies readonly Permission[];

export const PACKING_PERMISSIONS = [
  "packing.manage",
  "packing.pack",
  "packing.confirm",
  "packing.cancel",
  "packing.override_complete",
] as const satisfies readonly Permission[];

export const PACKING_PERMISSION_LABELS: Record<
  (typeof PACKING_PERMISSIONS)[number],
  string
> = {
  "packing.manage": "manage: see packing screens & materialize jobs",
  "packing.pack": "pack: start / evidence / seal / done",
  "packing.confirm": "confirm: send packed-confirmation email",
  "packing.cancel": "cancel: cancel a packing job",
  "packing.override_complete": "override_complete: re-open completed jobs",
};

export function isPackingPermission(p: string): p is (typeof PACKING_PERMISSIONS)[number] {
  return (PACKING_PERMISSIONS as readonly string[]).includes(p);
}

export type SectionId =
  | "dashboard"
  | "inventory"
  | "purchase_orders"
  | "sales"
  | "messages"
  | "packing"
  | "print_pack"
  | "customers"
  | "suppliers"
  | "channels"
  | "social"
  | "tiktok_shop"
  | "integrations"
  | "expenses"
  | "stationery"
  | "reports"
  | "settings"
  | "admin"
  | "activity_log"
  | "dropshipping"
  | "consignment"
  | "tasks"
  | "hr"
  | "returns"
  | "documents"
  | "asset_tracking"
  | "postage"
  | "intelligence"
  | "warehouse"
  | "compliance"
  | "account"
  | "evidence"
  | "automations"
  | "dynamic_pricing"
  | "ai_agents"
  | "perks"
  | "affiliates"
  | "consignment_splits"
  | "consignment_sync"
  | "exceptions"
  | "commerce"
  | "markets"
  | "customer_portfolios"
  | "operations"
  | "tenants";

export function useMe() {
  const queryClient = useQueryClient();
  const q = useGetMe({
    query: {
      queryKey: ["/api/me"],
      queryFn: ({ signal }) => withRequestTimeout(
        (requestSignal) => getMe({ signal: requestSignal }),
        {
          signal,
          timeoutMessage: "Your account access could not be verified within 15 seconds.",
        },
      ),
      staleTime: 60_000,
      // Permission changes must reach open screens without requiring a reload
      // or waiting for another navigation to make /api/me stale.
      refetchInterval: 15_000,
      refetchIntervalInBackground: false,
    },
  });
  const me = q.data as AuthedUser | undefined;
  const has = (perm: Permission) => Boolean(me?.permissions?.includes(perm));
  const canViewFinance = Boolean(me?.permissions?.includes("finance.business_pnl.view"));

  useEffect(() => {
    if (!me) return;
    const previous = financePermissionByClient.get(queryClient);
    if (
      previous?.userId === me.id &&
      previous.canViewFinance === canViewFinance
    ) return;
    financePermissionByClient.set(queryClient, {
      userId: me.id,
      canViewFinance,
    });

    const isSalesQuery = (query: { queryKey: readonly unknown[] }) =>
      typeof query.queryKey[0] === "string" &&
      query.queryKey[0].startsWith("/api/sales");
    if (canViewFinance) {
      // Revocation deliberately redacts cached Sales responses. When access
      // returns, active list/detail queries must fetch the authorized values
      // again instead of leaving those redacted cache entries on screen.
      if (previous?.userId === me.id && !previous.canViewFinance) {
        void queryClient.invalidateQueries({
          predicate: isSalesQuery,
          refetchType: "active",
        });
      }
      return;
    }

    void (async () => {
      await queryClient.cancelQueries({ predicate: isSalesQuery });
      const current = financePermissionByClient.get(queryClient);
      if (current?.userId !== me.id || current.canViewFinance) return;
      queryClient.setQueriesData(
        { predicate: isSalesQuery },
        (data: unknown) => redactSalesFinanceData(data),
      );
      await queryClient.invalidateQueries({
        predicate: isSalesQuery,
        refetchType: "active",
      });
    })();
  }, [canViewFinance, me?.id, queryClient]);
  // Admin always passes the section gate. For everyone else, an explicit
  // `true` value in the section map is required.
  const hasSection = (section: SectionId): boolean => {
    if (!me) return false;
    if (me.role === "owner" || me.role === "admin") return true;
    const map = (me.sections ?? {}) as Record<string, boolean | undefined>;
    return map[section] === true;
  };
  return {
    me,
    has,
    hasSection,
    isAdmin: me?.role === "owner" || me?.role === "admin",
    /** Cross-tenant flag — true grants Tenants admin page + future cross-tenant views. */
    isSuperAdmin: Boolean(me?.isSuperAdmin),
    isLoading: q.isLoading,
    error: q.error,
  };
}

/**
 * MetalUI platform-operator hook. Returns `true` only when the
 * backend confirms the caller is a super-admin AND a member of an
 * `is_platform=true` tenant. Drives:
 *   - visibility of the `/platform/*` sidebar entry (P3)
 *   - guard on the `/platform/feature-management` route
 *
 * Cheap server call (one indexed query); cached 60s like `useMe`.
 */
export function useIsPlatformOperator() {
  const q = useGetMyPlatformAccess({
    query: {
      queryKey: ["/api/me/platform-access"],
      queryFn: ({ signal }) => withRequestTimeout(
        (requestSignal) => getMyPlatformAccess({ signal: requestSignal }),
        {
          signal,
          timeoutMessage: "Platform access could not be checked within 15 seconds.",
        },
      ),
      retry: false,
      staleTime: 60_000,
    },
  });
  return {
    isPlatformOperator: Boolean(q.data?.hasPlatformAccess),
    // True only for operators in the canonical OWNER tenant; mirrors the
    // server's owner-only gate on toggles like `hr_module_enabled`.
    isOwnerOperator: Boolean(q.data?.isOwnerOperator),
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: q.refetch,
  };
}

/**
 * MetalUI feature-flag hook. Returns the set of `FeatureKey`s
 * currently enabled for the caller's tenant. Used by the upcoming
 * sidebar/route gating (P3) and by add-on page bodies to render an
 * "add-on disabled" upsell instead of 403'ing.
 */
export function useMyFeatures() {
  const q = useGetMyFeatures({
    query: { queryKey: ["/api/me/features"], staleTime: 60_000 },
  });
  const features = new Set<FeatureKey>(
    (q.data?.features ?? []) as FeatureKey[],
  );
  return {
    features,
    hasFeature: (key: FeatureKey) => features.has(key),
    // Task #1212: true when the tenant is suspended (trial lapse or manual).
    // The app shell uses this to lock everything except the Client Area.
    tenantSuspended: Boolean(q.data?.tenantSuspended),
    // Task #1238: true when the tenant is a demo tenant. Drives the Demo Mode
    // banner and "Connected (Demo)" integration statuses.
    isDemo: Boolean(q.data?.isDemo),
    // Task #1238: true when this demo tenant has demo sample data toggled on.
    demoDataEnabled: Boolean(q.data?.demoDataEnabled),
    // Distinguish a confirmed non-demo tenant from a failed or pending lookup.
    isReady: q.data !== undefined,
    isLoading: q.isLoading,
  };
}
